import { useMemo } from "react";
import type { CaseType } from "../utils/deriveCaseType";

export interface CompletionWarning {
  // data-field / name attribute used to scroll-and-focus the input
  field: string;
  label: string;
  // accordion section id to expand before scrolling
  section: string;
}

// Per-type fields that are expected but not hard-required. Missing values surface as a soft
// warning at Complete time (operator can complete anyway or jump to the field), never as a
// blocking form error — case-type-specific fields are always rendered and optional.
interface SpecimenValue {
  material: string;
  gross?: string | null;
  number: string;
}

interface CaseFieldValues {
  externalCaseNumber?: string | null;
  consultationReason?: string | null;
  specimens?: SpecimenValue[];
}

function isBlank(value: string | null | undefined): boolean {
  return !value || value.trim() === "";
}

const GROSS_VISIBLE_MATERIALS = new Set<string>([
  "block",
  "slides_only",
  "blocks_and_slides",
]);

/**
 * Compute the ordered list of missing-but-expected fields for the derived case type.
 * Returns an empty list when nothing is missing (completion can proceed directly).
 */
export function useCompletionWarnings(
  derivedCaseType: CaseType,
  caseValues: CaseFieldValues
): CompletionWarning[] {
  return useMemo(() => {
    const warnings: CompletionWarning[] = [];

    // External case number is expected for any case originating outside GCP.
    if (
      (derivedCaseType === "pass_through" ||
        derivedCaseType === "consultation" ||
        derivedCaseType === "tc_only") &&
      isBlank(caseValues.externalCaseNumber)
    ) {
      warnings.push({
        field: "case.externalCaseNumber",
        label: "External case number",
        section: "caseInfo",
      });
    }

    if (
      derivedCaseType === "consultation" &&
      isBlank(caseValues.consultationReason)
    ) {
      warnings.push({
        field: "case.consultationReason",
        label: "Consultation reason",
        section: "caseInfo",
      });
    }

    // G/pass-through cases usually require grossing if they are about to bypass it.
    // We warn if ANY gross-visible specimen has a blank gross.
    if (derivedCaseType === "pass_through") {
      const specimens = caseValues.specimens || [];
      specimens.forEach((spec, index) => {
        if (GROSS_VISIBLE_MATERIALS.has(spec.material) && isBlank(spec.gross)) {
          warnings.push({
            field: `case.specimens.${index}.gross`,
            label: `Gross description for Part ${spec.number}`,
            section: "specimens",
          });
        }
      });
    }

    return warnings;
  }, [
    derivedCaseType,
    caseValues.externalCaseNumber,
    caseValues.consultationReason,
    caseValues.specimens,
  ]);
}
